import { useEffect, useState } from "react";
import { api, formatApiError, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Plus, Check, X, Receipt } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  pending: "border-neutral-300 text-neutral-600",
  approved: "border-[#C9A961] text-[#8C6F1E]",
  rejected: "border-[#B33A1F] text-[#B33A1F]",
  reimbursed: "border-neutral-950 text-neutral-950",
};

export default function ExpensesPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "travel", amount: 0, date: new Date().toISOString().slice(0, 10), description: "", receipt_data_url: "" });

  const load = async () => {
    try { const { data } = await api.get("/expenses"); setRows(data); }
    catch (e) { toast.error("Failed"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const readFile = async (file) => {
    return new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount) });
      toast.success("Expense submitted"); setOpen(false);
      setForm({ category: "travel", amount: 0, date: new Date().toISOString().slice(0, 10), description: "", receipt_data_url: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Failed")); }
  };

  const decide = async (id, status) => {
    try { await api.patch(`/expenses/${id}`, { status }); toast.success(`Expense ${status}`); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const totals = rows.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + Number(r.amount || 0); return acc;
  }, {});

  return (
    <div>
      <PageHeader eyebrow="Reimbursements" title="Expenses." testid="page-expenses"
        description={user.role === "employee" ? "Submit bills for reimbursement. HR reviews here." : "Approve, reject or mark employee expense claims as reimbursed."}
        actions={user.role === "employee" && (
          <button onClick={() => setOpen(true)} data-testid="btn-new-expense"
            className="inline-flex items-center gap-2 bg-neutral-950 text-[#D4B87D] px-4 py-2.5 font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors">
            <Plus className="w-4 h-4" /> New claim
          </button>
        )}
      />
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {["pending", "approved", "rejected", "reimbursed"].map((s) => (
            <div key={s} className="border border-neutral-200 bg-white p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{s}</div>
              <div className="mt-1 font-serif font-black text-2xl tracking-tight">{fmtMoney(totals[s] || 0)}</div>
            </div>
          ))}
        </div>
        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="expenses-table">
            <thead className="bg-[#FAF7EF] border-b border-neutral-200">
              <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                <th className="px-4 py-3">Date</th>
                {user.role !== "employee" && <th className="px-4 py-3">Employee</th>}
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Receipt</th>
                <th className="px-4 py-3">Status</th>
                {user.role !== "employee" && <th className="px-4 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="p-6 font-mono text-neutral-500">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={8} className="p-8 font-mono text-neutral-500">No expense claims.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100 hover:bg-[#FAF7EF]">
                  <td className="px-4 py-3 font-mono">{r.date}</td>
                  {user.role !== "employee" && <td className="px-4 py-3">{r.employee_name}</td>}
                  <td className="px-4 py-3 font-mono capitalize">{r.category}</td>
                  <td className="px-4 py-3 font-mono text-right font-semibold">{fmtMoney(r.amount)}</td>
                  <td className="px-4 py-3 max-w-xs truncate">{r.description || "—"}</td>
                  <td className="px-4 py-3">{r.receipt_data_url ? <a href={r.receipt_data_url} target="_blank" rel="noreferrer" className="text-[#8C6F1E] underline text-xs font-mono">view</a> : <span className="text-neutral-300">—</span>}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border ${STATUS_COLOR[r.status]}`}>{r.status}</span></td>
                  {user.role !== "employee" && (
                    <td className="px-4 py-3 text-right">
                      {r.status === "pending" && (
                        <>
                          <button onClick={() => decide(r.id, "approved")} data-testid={`exp-approve-${r.id}`} className="text-[#8C6F1E] hover:text-neutral-950 font-mono text-xs mr-3 inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" />Approve</button>
                          <button onClick={() => decide(r.id, "rejected")} data-testid={`exp-reject-${r.id}`} className="text-[#B33A1F] hover:text-neutral-950 font-mono text-xs inline-flex items-center gap-1"><X className="w-3.5 h-3.5" />Reject</button>
                        </>
                      )}
                      {r.status === "approved" && (
                        <button onClick={() => decide(r.id, "reimbursed")} data-testid={`exp-reimburse-${r.id}`} className="text-neutral-950 hover:text-[#8C6F1E] font-mono text-xs">Mark reimbursed</button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-neutral-950/70 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border border-[#C9A961] w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
              <div><div className="text-[10px] font-mono uppercase tracking-widest text-[#8C6F1E]">Claim</div><div className="font-serif font-bold text-2xl tracking-tight">New expense</div></div>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Category</label>
                <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="exp-category" className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-mono text-sm bg-white">
                  {["travel","food","internet","supplies","client","other"].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Date</label>
                <input required type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} data-testid="exp-date" className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-mono text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Amount</label>
                <input required type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} data-testid="exp-amount" className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-mono text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Description</label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="exp-desc" className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-mono text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Receipt (image or PDF)</label>
                <input type="file" accept="image/*,application/pdf" data-testid="exp-file"
                  onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; if (f.size > 2500000) { toast.error("File too large (max 2.5 MB)"); return; } setForm({...form, receipt_data_url: await readFile(f)}); }}
                  className="text-xs font-mono" />
                {form.receipt_data_url && <div className="mt-2 text-xs text-[#8C6F1E] font-mono">Receipt attached ({Math.round(form.receipt_data_url.length/1024)} KB)</div>}
              </div>
              <button type="submit" data-testid="btn-submit-expense" className="col-span-2 px-5 py-3 bg-neutral-950 text-[#D4B87D] font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors flex items-center justify-center gap-2"><Receipt className="w-4 h-4" /> Submit claim</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
